import React from 'react'
import { Box, Text, HStack, VStack, useBreakpointValue } from '@chakra-ui/react'
import ProfileIcon from './ProfileIcon'
import { useWeather } from '../hooks/useWeather'
import { useLanguage } from '../i18n/LanguageContext'
import { useWeatherRegion } from '../hooks/useWeatherRegion'

interface HeaderProps {
  currentProfile: string;
  onProfileChange: (profile: string) => void;
  activeTab?: 'dashboard' | 'faq';
  onTabChange?: (tab: 'dashboard' | 'faq') => void;
}

const Header: React.FC<HeaderProps> = ({ currentProfile, onProfileChange, activeTab, onTabChange }) => {
  const { t } = useLanguage();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('goodMorning') : hour < 18 ? t('goodAfternoon') : t('goodEvening');
  const { weather, johor } = useWeather();
  const { region } = useWeatherRegion();
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const displayName = isDesktop ? 'Tony Tan' : currentProfile;

  return (
    <Box
      px={3}
      pt="max(env(safe-area-inset-top, 0px), 4px)"
      pb={2}
      w="100%"
    >

      {/* ── Row 1: greeting · name · profile icon ── */}
      <HStack justify="space-between" align="center" mb={0} spacing={0}>
        <Text
          fontSize="10px" fontWeight="800" letterSpacing="0.04em"
          textTransform="uppercase" color="rgba(255,255,255,0.8)"
          textShadow="0 1px 8px rgba(0,0,0,0.95)"
          flexShrink={0}
          whiteSpace="nowrap"
        >
          {greeting} !
        </Text>
        <Text
          fontSize={{ base: '32px', lg: '28px' }}
          fontWeight="900" color="white"
          letterSpacing="-0.03em" lineHeight="1.1" noOfLines={1}
          textShadow="0 1px 8px rgba(0,0,0,0.95)"
          flex={1}
          minW={0}
          textAlign="center"
          mx={1}
        >
          {displayName}
        </Text>
        <Box flexShrink={0}>
          <ProfileIcon currentProfile={currentProfile} onProfileChange={onProfileChange} activeTab={activeTab} onTabChange={onTabChange} />
        </Box>
      </HStack>

      {/* ── Row 2: Weather (always renders, fills in once loaded) ── */}
      {(() => {
        const SG_REGIONS = ['North', 'East', 'South', 'West', 'Central'];
        const sgTemp = weather?.regions['Central']?.tempC ?? null;

        return (
          <VStack spacing={1} w="100%" mt={-1}>
            {/* Singapore — always reserved space */}
            <Box px={3} pt={0} pb={1} w="100%">
              <HStack spacing={1.5} mb={1}>
                <Text fontSize="10px" fontWeight="600" letterSpacing="0.05em"
                  textTransform="uppercase" color="rgba(255,255,255,0.9)"
                  textShadow="0 1px 6px rgba(0,0,0,0.9)">
                  🇸🇬 {t('singapore')}
                </Text>
                <Text fontSize="10px" fontWeight="600" color="rgba(255,255,255,0.6)"
                  textShadow="0 1px 6px rgba(0,0,0,0.9)" minW="48px">
                  {sgTemp !== null ? `🌡️ ${sgTemp}°C` : '🌡️ —°C'}
                </Text>
              </HStack>
              <HStack spacing={0} w="auto" justify="flex-start">
                {SG_REGIONS.map((rgn) => {
                  const data = weather?.regions[rgn];
                  return (
                    <Box
                      key={rgn}
                      px={1}
                      py={0.5} borderRadius="6px"
                      display="flex" alignItems="center" justifyContent="center"
                      flexDirection="column" gap="1px"
                      minH="28px"
                    >
                      <Text fontSize="12px" lineHeight={1}>
                        {data?.emoji ?? '·'}
                      </Text>
                      <Text fontSize="9px" fontWeight="600" color="rgba(255,255,255,0.9)"
                        textShadow="0 1px 6px rgba(0,0,0,0.9)" whiteSpace="nowrap">
                        {rgn}
                      </Text>
                    </Box>
                  );
                })}
              </HStack>
            </Box>

            {/* Faint divider between Singapore and Johor Bahru */}
            {region === 'sg+jb' && (
              <Box
                w="60%"
                h="1px"
                mx="auto"
                bg="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)"
              />
            )}

            {/* Johor (only if enabled in settings) — also always reserved */}
            {region === 'sg+jb' && (
              <Box px={3} py={2} w="100%">
                <HStack spacing={1.5}>
                  <Text fontSize="10px" fontWeight="600" letterSpacing="0.05em"
                    textTransform="uppercase" color="rgba(255,255,255,0.9)"
                    textShadow="0 1px 6px rgba(0,0,0,0.9)">
                    🇲🇾 Johor Bahru
                  </Text>
                  <Text fontSize="10px" fontWeight="600" color="rgba(255,255,255,0.6)"
                    textShadow="0 1px 6px rgba(0,0,0,0.9)" minW="48px">
                    {johor?.tempC != null ? `🌡️ ${johor.tempC}°C` : '🌡️ —°C'}
                  </Text>
                  <Text fontSize="12px" lineHeight={1}>{johor?.emoji ?? '·'}</Text>
                </HStack>
              </Box>
            )}
          </VStack>
        );
      })()}
    </Box>
  )
}

export default Header
